<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Environmental Health Assessment</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Household Info -->
          <div v-if="household" class="alert alert-info mb-4">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="alert-heading mb-1">{{ household.household_number }}</h6>
                <p class="mb-0">
                  Head: {{ household.household_head_name || 'Not assigned' }} •
                  Members: {{ household.member_count || 0 }}
                </p>
              </div>
              <div v-if="lastAssessment" class="text-end">
                <small class="text-muted">
                  Last Assessment: {{ formatDate(lastAssessment.assessment_date) }}
                  <br>
                  Model Criteria Met: {{ lastAssessment.total_criteria_met || 0 }}/5
                </small>
              </div>
            </div>
          </div>

          <!-- Assessment Form -->
          <form @submit.prevent="submitAssessment">
            <!-- Water and Sanitation Section -->
            <div class="card mb-3">
              <div class="card-header bg-primary text-white">
                <h6 class="mb-0"><i class="bx bx-droplet me-2"></i>Water Supply & Quality</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Primary Water Source <span class="text-danger">*</span></label>
                    <select v-model="assessmentForm.waterSourceType" class="form-select" required>
                      <option value="">Select water source</option>
                      <option value="piped_home">Piped Water to Home</option>
                      <option value="piped_yard">Piped Water to Yard</option>
                      <option value="public_tap">Public Tap/Standpipe</option>
                      <option value="protected_well">Protected Well</option>
                      <option value="unprotected_well">Unprotected Well</option>
                      <option value="protected_spring">Protected Spring</option>
                      <option value="unprotected_spring">Unprotected Spring</option>
                      <option value="rainwater">Rainwater Collection</option>
                      <option value="surface_water">River/Lake/Pond</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Is Water Source Protected?</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.waterSourceProtected"
                             class="form-check-input" type="checkbox" id="waterProtectedSwitch">
                      <label class="form-check-label" for="waterProtectedSwitch">
                        {{ assessmentForm.waterSourceProtected ? 'Yes' : 'No' }}
                      </label>
                    </div>
                    <small class="text-muted">Protected from contamination</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Water Treatment Method</label>
                    <select v-model="assessmentForm.waterTreatmentMethod" class="form-select">
                      <option value="none">No Treatment</option>
                      <option value="boiling">Boiling</option>
                      <option value="chlorination">Chlorination/Water Guard</option>
                      <option value="filtering">Filtering</option>
                      <option value="solar_disinfection">Solar Disinfection</option>
                      <option value="multiple">Multiple Methods</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Water Storage</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.waterStorageSafe"
                             class="form-check-input" type="checkbox" id="waterStorageSwitch">
                      <label class="form-check-label" for="waterStorageSwitch">
                        {{ assessmentForm.waterStorageSafe ? 'Safe Storage' : 'Unsafe Storage' }}
                      </label>
                    </div>
                    <small class="text-muted">Covered container with tap/lid</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Sanitation Section -->
            <div class="card mb-3">
              <div class="card-header bg-warning text-dark">
                <h6 class="mb-0"><i class="bx bx-home-smile me-2"></i>Sanitation Facilities</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Latrine Available?</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.latrineAvailable"
                             class="form-check-input" type="checkbox" id="latrineSwitch"
                             @change="onLatrineChange">
                      <label class="form-check-label" for="latrineSwitch">
                        {{ assessmentForm.latrineAvailable ? 'Yes' : 'No' }}
                      </label>
                    </div>
                  </div>

                  <div v-if="assessmentForm.latrineAvailable" class="col-md-6 mb-3">
                    <label class="form-label">Latrine Type</label>
                    <select v-model="assessmentForm.latrineType" class="form-select">
                      <option value="">Select type</option>
                      <option value="flush_sewer">Flush to Sewer</option>
                      <option value="flush_septic">Flush to Septic Tank</option>
                      <option value="vip">Ventilated Improved Pit (VIP)</option>
                      <option value="pit_with_slab">Pit Latrine with Slab</option>
                      <option value="pit_without_slab">Pit Latrine without Slab</option>
                      <option value="composting">Composting Toilet</option>
                    </select>
                  </div>

                  <div v-if="assessmentForm.latrineAvailable" class="col-md-6 mb-3">
                    <label class="form-label">Latrine Condition</label>
                    <select v-model="assessmentForm.latrineCondition" class="form-select">
                      <option value="">Select condition</option>
                      <option value="excellent">Excellent - Clean & Well Maintained</option>
                      <option value="good">Good - Functional & Fairly Clean</option>
                      <option value="fair">Fair - Functional but Needs Cleaning</option>
                      <option value="poor">Poor - Barely Functional</option>
                      <option value="non_functional">Non-functional</option>
                    </select>
                  </div>

                  <div v-if="assessmentForm.latrineAvailable" class="col-md-6 mb-3">
                    <label class="form-label">Is Latrine Shared?</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.latrineShared"
                             class="form-check-input" type="checkbox" id="latrineSharedSwitch">
                      <label class="form-check-label" for="latrineSharedSwitch">
                        {{ assessmentForm.latrineShared ? 'Shared' : 'Private' }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Hygiene Section -->
            <div class="card mb-3">
              <div class="card-header bg-info text-white">
                <h6 class="mb-0"><i class="bx bx-wash me-2"></i>Hygiene Practices</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Handwashing Facility</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.handwashingFacility"
                             class="form-check-input" type="checkbox" id="handwashSwitch">
                      <label class="form-check-label" for="handwashSwitch">
                        {{ assessmentForm.handwashingFacility ? 'Available' : 'Not Available' }}
                      </label>
                    </div>
                    <small class="text-muted">Near latrine or kitchen</small>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Soap/Ash Available</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.soapAvailable"
                             class="form-check-input" type="checkbox" id="soapSwitch">
                      <label class="form-check-label" for="soapSwitch">
                        {{ assessmentForm.soapAvailable ? 'Available' : 'Not Available' }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Waste Management Section -->
            <div class="card mb-3">
              <div class="card-header bg-success text-white">
                <h6 class="mb-0"><i class="bx bx-trash me-2"></i>Waste Management</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Solid Waste Disposal Method</label>
                    <select v-model="assessmentForm.wasteDisposalMethod" class="form-select">
                      <option value="">Select method</option>
                      <option value="collection_service">Collection Service</option>
                      <option value="burning">Burning</option>
                      <option value="burying">Burying</option>
                      <option value="composting">Composting</option>
                      <option value="open_dumping">Open Dumping</option>
                      <option value="recycling">Recycling/Reuse</option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-label">Waste Disposal Safety</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.wasteDisposalSafe"
                             class="form-check-input" type="checkbox" id="wasteDisposalSwitch">
                      <label class="form-check-label" for="wasteDisposalSwitch">
                        {{ assessmentForm.wasteDisposalSafe ? 'Safe' : 'Unsafe' }}
                      </label>
                    </div>
                    <small class="text-muted">Away from water sources & living areas</small>
                  </div>

                  <div class="col-md-12 mb-3">
                    <label class="form-label">Animal Waste Management</label>
                    <select v-model="assessmentForm.animalWasteManagement" class="form-select">
                      <option value="not_applicable">No Animals</option>
                      <option value="proper_disposal">Proper Disposal/Composting</option>
                      <option value="biogas">Biogas System</option>
                      <option value="open_field">Open Field</option>
                      <option value="near_house">Near House (Unsafe)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Kitchen and Food Safety Section -->
            <div class="card mb-3">
              <div class="card-header bg-secondary text-white">
                <h6 class="mb-0"><i class="bx bx-restaurant me-2"></i>Kitchen & Food Safety</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Separate Kitchen</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.kitchenSeparate"
                             class="form-check-input" type="checkbox" id="kitchenSwitch">
                      <label class="form-check-label" for="kitchenSwitch">
                        {{ assessmentForm.kitchenSeparate ? 'Yes' : 'No' }}
                      </label>
                    </div>
                  </div>

                  <div class="col-md-4 mb-3">
                    <label class="form-label">Kitchen Ventilation</label>
                    <select v-model="assessmentForm.kitchenVentilation" class="form-select">
                      <option value="good">Good - Windows/Chimney</option>
                      <option value="fair">Fair - Some Ventilation</option>
                      <option value="poor">Poor - Smoky</option>
                    </select>
                  </div>

                  <div class="col-md-4 mb-3">
                    <label class="form-label">Food Storage</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.foodStorageSafe"
                             class="form-check-input" type="checkbox" id="foodStorageSwitch">
                      <label class="form-check-label" for="foodStorageSwitch">
                        {{ assessmentForm.foodStorageSafe ? 'Safe' : 'Unsafe' }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Vector Control Section -->
            <div class="card mb-3">
              <div class="card-header bg-danger text-white">
                <h6 class="mb-0"><i class="bx bx-bug me-2"></i>Vector Control</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-12 mb-3">
                    <label class="form-label">Vector Breeding Sites Observed</label>
                    <div class="form-check form-switch">
                      <input v-model="assessmentForm.vectorBreedingSites"
                             class="form-check-input" type="checkbox" id="vectorSwitch">
                      <label class="form-check-label" for="vectorSwitch">
                        {{ assessmentForm.vectorBreedingSites ? 'Yes - Action Needed' : 'No - Clear' }}
                      </label>
                    </div>
                    <small class="text-muted">Stagnant water, uncovered containers, etc.</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Recommendations Section -->
            <div class="card mb-3">
              <div class="card-header">
                <h6 class="mb-0"><i class="bx bx-clipboard me-2"></i>Recommendations & Follow-up</h6>
              </div>
              <div class="card-body">
                <div class="mb-3">
                  <label class="form-label">Key Recommendations</label>
                  <textarea v-model="assessmentForm.recommendations" class="form-control" rows="3"
                            placeholder="List specific improvements needed..."></textarea>
                </div>

                <div class="mb-3">
                  <div class="form-check form-switch">
                    <input v-model="assessmentForm.followUpNeeded"
                           class="form-check-input" type="checkbox" id="followUpSwitch">
                    <label class="form-check-label" for="followUpSwitch">
                      Follow-up Visit Required
                    </label>
                  </div>
                </div>

                <!-- Model Household Criteria Summary -->
                <div class="alert alert-warning">
                  <h6 class="alert-heading">Model Household Criteria Met: {{ modelCriteriaMet }}/5</h6>
                  <div class="row mt-2">
                    <div class="col-md-6">
                      <ul class="mb-0">
                        <li :class="assessmentForm.latrineAvailable ? 'text-success' : 'text-danger'">
                          <i :class="assessmentForm.latrineAvailable ? 'bx bx-check' : 'bx bx-x'"></i>
                          Functional Latrine
                        </li>
                        <li :class="assessmentForm.handwashingFacility ? 'text-success' : 'text-danger'">
                          <i :class="assessmentForm.handwashingFacility ? 'bx bx-check' : 'bx bx-x'"></i>
                          Handwashing Facility
                        </li>
                        <li :class="assessmentForm.kitchenSeparate ? 'text-success' : 'text-danger'">
                          <i :class="assessmentForm.kitchenSeparate ? 'bx bx-check' : 'bx bx-x'"></i>
                          Separate Kitchen
                        </li>
                      </ul>
                    </div>
                    <div class="col-md-6">
                      <ul class="mb-0">
                        <li :class="assessmentForm.wasteDisposalSafe ? 'text-success' : 'text-danger'">
                          <i :class="assessmentForm.wasteDisposalSafe ? 'bx bx-check' : 'bx bx-x'"></i>
                          Safe Waste Disposal
                        </li>
                        <li :class="assessmentForm.waterStorageSafe ? 'text-success' : 'text-danger'">
                          <i :class="assessmentForm.waterStorageSafe ? 'bx bx-check' : 'bx bx-x'"></i>
                          Safe Water Storage
                        </li>
                      </ul>
                    </div>
                  </div>
                  <p v-if="modelCriteriaMet >= 4" class="mb-0 mt-2 text-success">
                    <strong>Qualifies as Model Household!</strong>
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="button" class="btn btn-primary" @click="submitAssessment"
                  :disabled="!isFormValid || submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Saving...
            </span>
            <span v-else>
              <i class="bx bx-save"></i> Save Assessment
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "EnvironmentalAssessmentModal",
  props: {
    household: {
      type: Object,
      default: null
    },
    householdId: {
      type: [Number, String],
      required: true
    },
    existingAssessment: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      submitting: false,
      lastAssessment: null,

      assessmentForm: {
        // Water and Sanitation
        waterSourceType: '',
        waterSourceProtected: false,
        waterTreatmentMethod: 'none',
        waterStorageSafe: false,

        // Latrine
        latrineAvailable: false,
        latrineType: '',
        latrineCondition: '',
        latrineShared: false,

        // Hygiene
        handwashingFacility: false,
        soapAvailable: false,

        // Waste Management
        wasteDisposalMethod: '',
        wasteDisposalSafe: false,
        animalWasteManagement: 'not_applicable',

        // Kitchen and Food
        kitchenSeparate: false,
        kitchenVentilation: 'fair',
        foodStorageSafe: false,

        // Vector Control
        vectorBreedingSites: false,

        // Recommendations
        recommendations: '',
        followUpNeeded: false
      }
    };
  },

  computed: {
    modelCriteriaMet() {
      let count = 0;
      if (this.assessmentForm.latrineAvailable) count++;
      if (this.assessmentForm.handwashingFacility) count++;
      if (this.assessmentForm.kitchenSeparate) count++;
      if (this.assessmentForm.wasteDisposalSafe) count++;
      if (this.assessmentForm.waterStorageSafe) count++;
      return count;
    },

    isFormValid() {
      return this.assessmentForm.waterSourceType !== '';
    }
  },

  mounted() {
    console.log('Environmental modal mounted');
    console.log('Household:', this.household);
    console.log('Household ID:', this.householdId);

    if (this.existingAssessment) {
      this.loadExistingAssessment();
    }

    this.loadLastAssessment();
  },

  methods: {
    loadExistingAssessment() {
      if (this.existingAssessment) {
        // Load existing assessment data into form
        Object.keys(this.assessmentForm).forEach(key => {
          if (this.existingAssessment[key] !== undefined) {
            this.assessmentForm[key] = this.existingAssessment[key];
          }
        });

        this.lastAssessment = this.existingAssessment;
      }
    },

    async loadLastAssessment() {
      // This would load the last assessment to show progress
      // For now, we'll skip this as it's not critical
    },

    onLatrineChange() {
      if (!this.assessmentForm.latrineAvailable) {
        this.assessmentForm.latrineType = '';
        this.assessmentForm.latrineCondition = '';
        this.assessmentForm.latrineShared = false;
      }
    },

    async submitAssessment() {
      console.log('Submit assessment called');
      if (!this.isFormValid) {
        console.log('Form is not valid');
        return;
      }

      this.submitting = true;
      console.log('Preparing data for API call');

      const data = {
        "function": "recordEnvironmentalAssessment",
        "householdId": this.householdId || this.household?.household_id,
        "assessmentDate": new Date().toISOString().split('T')[0],
        ...this.assessmentForm
      };

      console.log('API data:', data);

      try {
        const res = await executeJson(data, authUrl);
        console.log('API response:', res);

        if (res.data.success) {
          this.$emit('saved', {
            assessmentId: res.data.data.assessmentId,
            modelCriteriaMet: res.data.data.modelHouseholdCriteriaMet
          });
        } else {
          alert(res.data.message || 'Failed to save assessment');
        }
      } catch (e) {
        console.error("Failed to save assessment:", e);
        console.error("Error details:", e.response);
        alert('Failed to save assessment. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.card {
  border: 1px solid #dee2e6;
}

.card-header {
  font-weight: 600;
}

.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}

.alert ul {
  padding-left: 20px;
  list-style: none;
}

.alert ul li {
  margin-bottom: 5px;
}

.text-success .bx-check {
  font-weight: bold;
}

.text-danger .bx-x {
  font-weight: bold;
}
</style>